import { useState, useContext, useReducer } from "react"
import {
  RecurringDonation,
  Designation,
  RecurringDonationStatus,
} from "../types"
import { formattedCents } from "source/giving/utils/money"
import { Heading, Button } from "@planningcenter/doxy-web"
import { usePaymentMethodsContext } from "../hooks/usePaymentMethodsContext"
import { useQuery, useQueryClient, InfiniteData } from "@tanstack/react-query"
import { fetchFunds, parseRecurringDonationResponse } from "../queries"
import PaymentMethodSelector from "./PaymentMethodSelector"
import EditDesignations from "./EditDesignations"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { includedDesignationsToData } from "../queries"
import { AlertMessage } from "source/shared/components"
import DeleteConfirmationDialog from "source/giving/profile/components/DeleteConfirmationDialog"
import EditSchedule from "./EditSchedule"
import { Schedule } from "source/giving/donate/form/types"
import { scheduleStateToAttributes } from "../utils/frequency"
import { reducer } from "./stores/reducer"
import { initializeState } from "./stores/initialState"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { fetchStripeObject } from "../queries"
import { launchAsyncModal } from "source/payments/utils"
import AddPaymentMethodModal from "./AddPaymentMethodModal"
import {
  useValidateRecurringDonation,
  extractMessagesFrom,
} from "./stores/validations"
import { APIError } from "source/shared/types"

function extractApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const responseError = error as { response?: { data?: APIError } }
    if (responseError.response?.data) {
      const apiError = responseError.response.data
      if (apiError.errors && apiError.errors.length > 0) {
        return apiError.errors[0].detail
      }
    }
  }
  return "Something went wrong. Please try again."
}

interface EditRecurringDonationFormProps {
  recurringDonation: RecurringDonation
  donationTotalAmount: number
  onCancel: () => void
}

export default function EditRecurringDonationForm({
  recurringDonation,
  donationTotalAmount,
  onCancel,
}: EditRecurringDonationFormProps) {
  const { paymentMethods, refreshPaymentMethods } = usePaymentMethodsContext()
  const { stripe, current_organization } = useContext(ServerRenderedProps)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [state, dispatch] = useReducer(
    reducer,
    recurringDonation,
    initializeState,
  )
  const queryClient = useQueryClient()

  const {
    designations,
    selectedPaymentMethodId,
    initialPaymentMethodId,
    schedule,
    status,
    initialStatus,
    releaseHoldAt,
    nextOccurrence,
    validationErrors,
    apiErrorMessage,
  } = state

  const {
    data: funds,
    isLoading: isLoadingFunds,
    isError: isErrorFunds,
  } = useQuery({
    queryKey: ["Funds"],
    queryFn: () => fetchFunds(),
  })

  const { data: stripeObject } = useQuery({
    queryKey: ["StripeObject"],
    queryFn: () =>
      fetchStripeObject({
        publishable_key: stripe.publishable_key,
        account_identifier: stripe.account_identifier,
      }),
    enabled: !!stripe?.publishable_key && !!stripe?.account_identifier,
  })

  const validateRecurringDonation = useValidateRecurringDonation(
    {
      designations,
      selectedPaymentMethodId,
    },
    dispatch,
  )

  const handleChangeDesignations = (designations: Designation[]) => {
    dispatch({ type: "UPDATE_DESIGNATIONS", designations })
  }

  const headingText = `${formattedCents(donationTotalAmount)} ${recurringDonation.humanized_schedule}`

  const handleAddPaymentMethodClick = async () => {
    await launchAsyncModal(AddPaymentMethodModal, {
      onSuccess: (newPaymentMethodId: string) => {
        refreshPaymentMethods()
        dispatch({
          type: "UPDATE_PAYMENT_METHOD",
          paymentMethodId: newPaymentMethodId,
        })
      },
      stripe,
      organizationId: current_organization.id,
      stripeObject,
    })
  }

  async function handleDeleteClick() {
    setShowDialog(false)
    setIsSaving(true)
    dispatch({ type: "CLEAR_ALL_ERRORS" })

    try {
      await sessionApiClient.del(
        `/giving/v2/me/recurring_donations/${recurringDonation.id}`,
      )
      queryClient.setQueryData(
        ["RecurringDonations"],
        (
          data: InfiniteData<{
            recurringDonations: RecurringDonation[]
          }>,
        ) => {
          if (!data) return data

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              recurringDonations: page.recurringDonations.filter(
                (item) => item.id !== recurringDonation.id,
              ),
            })),
          }
        },
      )
      onCancel()
    } catch (error: unknown) {
      const errorMessage = extractApiErrorMessage(error)
      dispatch({ type: "SET_API_ERROR", errorMessage })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelClick = () => {
    onCancel()
  }

  async function handleUpdateClick() {
    const isValid = validateRecurringDonation()

    if (!isValid) {
      return
    }

    setIsSaving(true)
    dispatch({ type: "CLEAR_ALL_ERRORS" })

    scheduleStateToAttributes(
      recurringDonation,
      schedule,
      status,
      releaseHoldAt,
      nextOccurrence,
    )
    const included = includedDesignationsToData(designations)

    const attributes: {
      release_hold_at: string | null
      on_hold_indefinitely: boolean
      schedule: typeof recurringDonation.schedule
      next_occurrence?: string | null
    } = {
      release_hold_at: recurringDonation.release_hold_at,
      on_hold_indefinitely: recurringDonation.on_hold_indefinitely,
      schedule: recurringDonation.schedule,
      ...(nextOccurrence && {
        next_occurrence: recurringDonation.next_occurrence,
      }),
    }

    const requestPayload = {
      data: {
        attributes,
        relationships: {
          payment_method: {
            data: { type: "PaymentMethod", id: selectedPaymentMethodId },
          },
        },
      },
      included: included,
    }

    try {
      await sessionApiClient.patch(
        `/giving/v2/me/recurring_donations/${recurringDonation.id}`,
        requestPayload,
      )
      const response = await sessionApiClient.get(
        `/giving/v2/me/recurring_donations/${recurringDonation.id}?include=designations,designations.fund`,
      )
      const refetchedDonation = parseRecurringDonationResponse(response)

      queryClient.setQueryData(
        ["RecurringDonations"],
        (
          data: InfiniteData<{
            recurringDonations: RecurringDonation[]
          }>,
        ) => {
          if (!data) return data
          if (!refetchedDonation) return data

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              recurringDonations: page.recurringDonations.map((item) =>
                item.id === refetchedDonation.id ? refetchedDonation : item,
              ),
            })),
          }
        },
      )
      onCancel()
    } catch (error: unknown) {
      const errorMessage = extractApiErrorMessage(error)
      dispatch({ type: "SET_API_ERROR", errorMessage })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div>
      <header className="mb-2">
        <Heading level={3} size={4} text="Manage recurring donation" />
        <p className="mt-4p c-tint2">{headingText}</p>
      </header>
      {apiErrorMessage && (
        <AlertMessage type="error" icon="general#exclamation-triangle">
          {apiErrorMessage}
        </AlertMessage>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <div className="mb-2">
          {extractMessagesFrom(validationErrors, "paymentMethod").map(
            (message, index) => (
              <AlertMessage
                key={index}
                type="error"
                icon="general#exclamation-triangle"
              >
                {message}
              </AlertMessage>
            ),
          )}
          {extractMessagesFrom(validationErrors, "totalAmount").map(
            (message, index) => (
              <AlertMessage
                key={index}
                type="error"
                icon="general#exclamation-triangle"
              >
                {message}
              </AlertMessage>
            ),
          )}
        </div>
      )}
      {!isLoadingFunds && !isErrorFunds && funds && (
        <>
          <EditDesignations
            designations={designations}
            funds={funds}
            onChangeDesignations={handleChangeDesignations}
            validationErrors={validationErrors}
            dispatch={dispatch}
          />
          <PaymentMethodSelector
            onAddClick={handleAddPaymentMethodClick}
            onChange={(id: string | null) =>
              dispatch({ type: "UPDATE_PAYMENT_METHOD", paymentMethodId: id })
            }
            paymentMethods={paymentMethods}
            selectedId={selectedPaymentMethodId}
            currentPaymentMethodId={initialPaymentMethodId}
          />
          <EditSchedule
            schedule={schedule}
            status={status}
            initialStatus={initialStatus}
            releaseHoldAt={releaseHoldAt}
            nextOccurrence={nextOccurrence}
            onChangeFrequency={(frequency: Schedule) =>
              dispatch({ type: "UPDATE_SCHEDULE", schedule: frequency })
            }
            onChangeStatus={(status: RecurringDonationStatus) =>
              dispatch({ type: "UPDATE_STATUS", status })
            }
            onChangeReleaseHoldAt={(releaseHoldAt: string | null) =>
              dispatch({ type: "UPDATE_RELEASE_HOLD_AT", releaseHoldAt })
            }
            onChangeNextOccurrence={(nextOccurrence: string | null) =>
              dispatch({ type: "UPDATE_NEXT_OCCURRENCE", nextOccurrence })
            }
          />
          {/* sm device buttons layout */}
          <div className="mt-2 d-n d-f@sm ai-c jc-sb">
            <Button
              onClick={() => setShowDialog(true)}
              text="Delete"
              theme="destroy"
              size="md"
              disabled={isSaving}
            />
            <div className="d-f g-1">
              <Button
                onClick={handleCancelClick}
                text="Cancel"
                variant="naked"
                size="md"
                disabled={isSaving}
              />
              <Button
                onClick={handleUpdateClick}
                text="Update donation"
                size="md"
                disabled={isSaving}
              />
            </div>
          </div>
          {/* md/lg device buttons layout */}
          <div className="mt-2 d-f fd-c g-2 d-n@sm">
            <Button
              onClick={handleUpdateClick}
              text="Update donation"
              size="md"
              disabled={isSaving}
            />
            <Button
              onClick={() => setShowDialog(true)}
              text="Delete"
              theme="destroy"
              size="md"
              disabled={isSaving}
            />
            <Button
              onClick={handleCancelClick}
              text="Cancel"
              variant="naked"
              size="md"
              disabled={isSaving}
            />
          </div>{" "}
        </>
      )}

      {showDialog && (
        <DeleteConfirmationDialog
          label="Delete recurring donation?"
          description="This will remove all scheduled occurrences of this donation. If you change your mind, you'll have to create a new recurring donation."
          deleteButtonText="Delete"
          onCancel={() => setShowDialog(false)}
          onDelete={() => handleDeleteClick()}
        />
      )}
    </div>
  )
}
